import { iconSize } from '@moderneinc/design-system-tokens';
import { alpha, Box, MenuList } from '@mui/material';
import { useRouter } from 'next/router';
import type React from 'react';
import type { FunctionComponent } from 'react';
import { useFormContext } from 'react-hook-form';

import { useAutoCompleteResultsQuery } from '../../__generated__/apollo-hooks';
import { ERROR_MESSAGES } from '../../constants/error-messages';
import { DOHERTY_THRESHOLD } from '../../constants/general';
import {
  handleMiddleClickOpen,
  recipeDetailsUrl
} from '../../helpers/link.helper';
import { useSelectedOrganization } from '../../hooks/use-organizations.hooks';
import { SearchIcon } from '../../icons/icons';
import {
  selectActiveStepId,
  useTourEngineStore
} from '../../stores/tour-engine.store';
import { DAY_ZERO_RECIPE_ID } from '../guided-tour/day-zero-tour.config';
import { CenteredBox } from '../styled-components/layouts/layouts.styled';
import { EmptyState } from '../utilities/empty-state/empty-state.component';
import { Loading } from '../utilities/loading/loading.component';
import { CompactRecipeSearchResult } from './compact-recipe-search-result.component';
import { RecipeListItemButton, SectionHeading } from './global-search.styled';

type SearchRecipesProps = {
  searchTerm: string;
  resetModal: React.Dispatch<React.SetStateAction<null>>;
  toggleMenu?: () => void;
  onRecipeSelect?: () => void;
};

export const SearchRecipes: FunctionComponent<SearchRecipesProps> = ({
  searchTerm,
  resetModal,
  toggleMenu,
  onRecipeSelect
}) => {
  const router = useRouter();
  const { selectedOrganization } = useSelectedOrganization();

  const { resetField } = useFormContext();
  const activeTourStepId = useTourEngineStore(selectActiveStepId);
  const isChooseRecipeStep = activeTourStepId === 'choose-recipe';
  // The tour drives the search dialog across these three steps. A click on any
  // recipe but Common Static Analysis must be a no-op throughout — not just on
  // choose-recipe — otherwise navigating away closes the dialog and strands the
  // tour on a target (the dialog input / spotlighted row) that no longer exists.
  const isTourDrivingSearch =
    activeTourStepId === 'open-search' ||
    activeTourStepId === 'type-recipe' ||
    isChooseRecipeStep;
  const goToStep = useTourEngineStore((state) => state.goToStep);

  const resetState = () => {
    resetField('query');
    resetModal(null);
    if (toggleMenu) {
      toggleMenu();
    }
  };

  const { data, loading } = useAutoCompleteResultsQuery({
    variables: {
      organizationId: selectedOrganization?.id,
      query: searchTerm
    },
    skip: !selectedOrganization?.id,
    fetchPolicy: 'cache-first'
  });

  const recipes = data?.organization?.marketplace?.recipes;

  const handleRecipeNavigation = (id: string) => () => {
    // During the Day Zero tour, only the Common Static Analysis recipe may be
    // selected — clicking any other recipe is a no-op so a first-time user
    // can't wander off the guided path.
    if (isTourDrivingSearch && id !== DAY_ZERO_RECIPE_ID) {
      return;
    }
    // Selecting the tour recipe jumps straight to the recipe-details step from
    // any search step. Advancing by one from type-recipe would land on
    // choose-recipe, whose target unmounts with the closing dialog — stranding
    // the tour until it times out and pauses.
    if (isTourDrivingSearch && id === DAY_ZERO_RECIPE_ID) {
      goToStep('recipe-details');
    }
    onRecipeSelect?.();
    resetState();
    router.push(recipeDetailsUrl({ id }));
  };

  const handleItemKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') {
      return;
    }
    e.preventDefault();
    e.stopPropagation();

    const list = (e.currentTarget as HTMLElement).closest<HTMLElement>(
      'ul[role="menu"]'
    );
    if (!list) {
      return;
    }
    const focusable = Array.from(
      list.querySelectorAll<HTMLElement>('[tabindex="0"]')
    );
    const current = focusable.indexOf(e.currentTarget as HTMLElement);
    if (current === -1) {
      return;
    }

    const next =
      e.key === 'ArrowDown'
        ? (current + 1) % focusable.length
        : (current - 1 + focusable.length) % focusable.length;
    focusable.at(next)?.focus();
  };

  return (
    <Box
      sx={{
        width: '100%'
      }}
    >
      <SectionHeading>Recipes</SectionHeading>
      <Box
        sx={{
          overflow: 'auto',
          flex: 1
        }}
      >
        {!data && loading && (
          <CenteredBox
            sx={{
              height: '100%'
            }}
          >
            <Loading caption="Searching for recipes" wait={DOHERTY_THRESHOLD} />
          </CenteredBox>
        )}
        <MenuList sx={{ px: 2.5 }}>
          {/* Not a `MenuItem`: an unselectable row still joins the list's
              arrow-key order, so the footer offers "Enter Select" over it. */}
          {recipes?.edges?.length === 0 && (
            <EmptyState
              icon={<SearchIcon size={iconSize.xxl} />}
              {...ERROR_MESSAGES.recipes.searchNoneFound}
            />
          )}

          {recipes?.edges?.map(({ node: { id, instanceName, name } }) => (
            <RecipeListItemButton
              key={id}
              data-tour={
                id === DAY_ZERO_RECIPE_ID ? 'choose-recipe' : undefined
              }
              onClick={handleRecipeNavigation(id)}
              onMouseDown={handleMiddleClickOpen(recipeDetailsUrl({ id }))}
              onKeyDown={handleItemKeyDown}
              tabIndex={0}
              sx={(theme) => {
                // During the tour, spotlight the target recipe and fade the
                // rest (they're also click-blocked above) so the right choice
                // is obvious without a list-blocking overlay.
                if (!isChooseRecipeStep) {
                  return {};
                }
                return id === DAY_ZERO_RECIPE_ID
                  ? {
                      backgroundColor: alpha(
                        theme.palette.primary.main,
                        theme.palette.action.selectedOpacity
                      )
                    }
                  : { opacity: 0.4 };
              }}
            >
              <CompactRecipeSearchResult id={id} name={instanceName || name} />
            </RecipeListItemButton>
          ))}
        </MenuList>
      </Box>
    </Box>
  );
};
